import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Form, Input, Radio, Select, Spin, message } from "antd";
import { useEffect, useState } from "react";
import { MdOutlineDeleteForever } from "react-icons/md";
import { useIntl } from "react-intl";
import PhoneInputWithCountrySelect, {
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import {
  adminAddClient,
  adminDeleteClient,
  adminShowClient,
  adminUpdateClient,
  getLocationsAdmin,
} from "../../network/admin";
import queryClient from "../../services/react-query/queryClient";
import { queryKeys } from "../../services/react-query/queryKeys";
import DeleteModal from "../common/DeleteModal";
import GenderSwitch from "../common/GenderSwitch";
import UserAppointments from "./UserAppointments";

type PopAddNewClientProps = {
  close: () => void;
  clientId?: number;
  dataClientModal?: any;
};
const PopAddNewClientAdmin = ({
  close,
  clientId,
  dataClientModal,
}: PopAddNewClientProps) => {
  const intl = useIntl();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [active, setActive] = useState("");
  const [activeStaff, setActiveStaff] = useState("");
  const { data: locations, isLoading: fetchingSalons } = useQuery(
    ["AdminLocations"],
    () => getLocationsAdmin(),
    {
      select: (res) => res?.data,
    }
  );
  const salonOptions = Array.isArray(locations)
    ? locations?.map((location) => ({
        label: location?.name,
        value: location?.salon_id,
      }))
    : [];
  useEffect(() => {
    setActive(dataClientModal?.gender || "");
    setActiveStaff(dataClientModal?.preferred_staff_gender || "");
  }, [dataClientModal]);
  //! use x-translations = true when change the client name to EN & AR
  const { data: clientDetails, isLoading: fetchingClientDetails } = useQuery(
    [queryKeys.listClient, clientId],
    () => adminShowClient(clientId),
    {
      select: (res) => res?.data,
      enabled: !!clientId,
    }
  );
  const [form] = Form.useForm();
  useEffect(() => {
    const initialData = {
      ...clientDetails,
      salon_ids:
        clientDetails?.salons?.map((salon: { id: number }) => {
          return salon?.id;
        }) || [],
    };
    form.setFieldsValue(initialData);
    setActive(clientDetails?.gender?.toLowerCase() || "");
    setActiveStaff(clientDetails?.preferred_staff_gender?.toLowerCase() || "");
  }, [clientDetails]);
  const {
    mutate: onAddClient,
    isLoading: isAddingClient,
    isSuccess: isAddClientSuccess,
    error: addClientError,
  } = useMutation(adminAddClient, {
    onSuccess: (res: any) => {
      if (res?.code === "ERR_BAD_REQUEST") {
        message.error(res?.response?.data?.message);
      } else {
        message.success("Client created successfully");
        queryClient.invalidateQueries(["clients"]);
        close();
      }
    },
    onError: (error) => {
      message.error("Failed to add client. Please try again.");
    },
  });
  const { mutate: onUpdateClient, isLoading } = useMutation(
    (params) => adminUpdateClient(clientId, params),
    {
      onSuccess: (res: any) => {
        if (res?.code === "ERR_BAD_REQUEST") {
          message.error(res?.response?.data?.message);
        } else {
          queryClient.invalidateQueries(["clients"]);
          message.success("Submit success!");
          close();
        }
      },
      onError: (error) => {
        message.error("حاول مرة اخرى");
      },
    }
  );

  const onFinish = async (values: any) => {
    const phoneNumber = parsePhoneNumber(values?.phone || "");

    const clientData = {
      ...values,
      phone_country_code: phoneNumber ? phoneNumber.country : "",
      phone: phoneNumber ? phoneNumber.nationalNumber : "",
      gender: active,
      preferred_staff_gender: activeStaff,
    };

    try {
      if (clientId) {
        await onUpdateClient(clientData);
      } else {
        await onAddClient(clientData);
      }
    } catch {}
  };

  const changeGender = (e: any) => {
    setActive(e.target.value);
  };
  const changeStaffGender = (e: any) => {
    setActiveStaff(e.target.value);
  };

  return (
    <div className=" p-6 max-lg:p-3 min-w-[50%]">
      <div className="bg-white rounded-lg">
        <div className="grid items-center justify-center w-full text-center">
          <h4 className="text-color-primary font-semibold text-2xl">
            {clientId
              ? intl.formatMessage({
                  id: "editCustomerData",
                  defaultMessage: "تعديل بيانات العميل",
                })
              : intl.formatMessage({
                  id: "addANewCustomer",
                  defaultMessage: "إضافة عميل جديد",
                })}
          </h4>
          <span className="mt-4 text-[#75757E] text-base">
            {clientId
              ? intl.formatMessage({
                  id: "updateCustomerDataInfo",
                  defaultMessage:
                    "قم بتحديث المعلومات التالية لتحديث بيانات العميل في مشروعك",
                })
              : intl.formatMessage({
                  id: "subTitleAddNewCustomer",
                  defaultMessage:
                    "قم بتعبئة المعلومات التالية لإضافة عميل جديد إلى مشروعك",
                })}
          </span>
        </div>
      </div>
      {clientId && fetchingClientDetails ? (
        <div className="w-full h-full flex justify-center items-center">
          <Spin size="large" />
        </div>
      ) : (
        <Form name="step1" onFinish={onFinish} layout="vertical" form={form}>
          <div className="bg-white lg:grid grid-cols-1 lg:grid-cols-2  gap-4 w-full rounded-lg mt-8">
            <div className="flex gap-2">
              <Form.Item
                label={intl.formatMessage({
                  id: "firstName",
                })}
                name="first_name"
                className="w-full !mb-0"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "firstNameRequired",
                    }),
                  },
                ]}
                required={false}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={intl.formatMessage({
                  id: "lastName",
                })}
                name="last_name"
                className="w-full !mb-0"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "lastNameRequired",
                    }),
                  },
                ]}
                required={false}
              >
                <Input />
              </Form.Item>
            </div>
            <Form.Item
              name="salon_ids"
              label="Associated Salon"
              required={false}
              rules={[
                {
                  required: true,
                  message: "Associated Salon is required",
                },
              ]}
            >
              <Select
                loading={fetchingSalons}
                mode="multiple"
                placeholder="Associated Salon"
                options={salonOptions}
              />
            </Form.Item>
            <Form.Item
              label={intl.formatMessage({
                id: "email",
              })}
              name="email"
              rules={[
                {
                  type: "email",
                  message: intl.formatMessage({
                    id: "invalidEmail",
                  }),
                },
              ]}
            >
              <Input disabled={dataClientModal} />
            </Form.Item>
            <Form.Item
              name="phone"
              label={intl.formatMessage({
                id: "phone",
              })}
              className="w-full max-md:!mb-10 !mb-0"
              // hasFeedback
              // disabled={dataClientModal}
              required={false}
              colon={false}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "phoneRequired",
                    defaultMessage: "رقم جوال مطلوب",
                  }),
                },
                {
                  validator: (rule, value) => {
                    if (isValidPhoneNumber(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      intl.formatMessage({
                        id: "phoneInvalid",
                        defaultMessage: "رقم الجوال غير صالح",
                      })
                    );
                  },
                },
              ]}
            >
              <PhoneInputWithCountrySelect
                defaultCountry="SA"
                className={`w-full max-md:!mb-10 !mb-0 ${
                  dataClientModal || clientId ? "phone-input-disabled" : ""
                }`}
                onChange={(value) => {}}
                name="phone_country_code"
                international
                hasFeedback
                limitMaxLength={true}
                disabled={!!clientId}
              />
            </Form.Item>

            <Form.Item
              name="gender"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "PleaseSpecifyGender",
                  }),
                },
              ]}
              required={false}
              className="col-span-2 w-full !-mt-4"
            >
              <div className="flex max-lg:flex-col items-start justify-between col-span-2 text-start">
                <div className="max-lg:mb-3">
                  {intl.formatMessage({
                    id: "gender",
                  })}
                </div>

                <Radio.Group
                  onChange={changeGender}
                  defaultValue={dataClientModal?.gender}
                >
                  <div className="flex gap-2 justify-between">
                    <GenderSwitch
                      active={"male" === active}
                      gender="male"
                      string={intl.formatMessage({
                        id: "manGender",
                      })}
                    />

                    <GenderSwitch
                      active={"female" === active}
                      gender="female"
                      string={intl.formatMessage({
                        id: "womanGender",
                      })}
                    />
                  </div>
                </Radio.Group>
              </div>
            </Form.Item>
            <Form.Item
              name="preferred_staff_gender"
              required={false}
              className="col-span-2 w-full  !-mt-6"
            >
              <div className="flex max-lg:grid items-center justify-between col-span-2">
                <div className="max-lg:mb-3">
                  {intl.formatMessage({
                    id: "iWouldLikeToDealWith",
                  })}
                </div>
                <Radio.Group onChange={changeStaffGender}>
                  <div className="flex gap-2 justify-between">
                    <GenderSwitch
                      active={"male" === activeStaff}
                      gender="male"
                      string={intl.formatMessage({
                        id: "manGender",
                      })}
                    />
                    <GenderSwitch
                      active={"female" === activeStaff}
                      gender="female"
                      string={intl.formatMessage({
                        id: "womanGender",
                      })}
                    />
                  </div>
                </Radio.Group>
              </div>
            </Form.Item>
          </div>
          <div className="-mt-2 max-h-[500px] overflow-auto">
            {clientId ? (
              <UserAppointments appointments={clientDetails?.appointments} />
            ) : null}
            <div className="col-span-2 w-full">
              <Form.Item className="">
                <div className="flex gap-2">
                  <Button
                    className="w-full"
                    type="primary"
                    htmlType="submit"
                    loading={isLoading || isAddingClient}
                  >
                    {clientId
                      ? intl.formatMessage({
                          id: "Save Changes",
                        })
                      : intl.formatMessage({
                          id: "AddMember",
                        })}
                  </Button>
                  <Button
                    onClick={close}
                    type="primary"
                    className="btn-primary-not"
                  >
                    {intl.formatMessage({
                      id: "cancel",
                    })}
                  </Button>
                </div>
              </Form.Item>
            </div>
          </div>
        </Form>
      )}
      {clientDetails ? (
        <div className="text-[#C43535] text-base font-medium flex items-center justify-center mt-8">
          <DeleteModal
            visible={openDeleteModal}
            onClose={() => {
              setOpenDeleteModal(false);
            }}
            onDelete={adminDeleteClient} // Pass the delete function for "Package"
            data={clientId}
            confirmationHeader={"Are you sure you want to delete this Client?"}
            confirmationText={""}
            queryKey={"clients"} // Pass the correct key here
            successMessage={"Client deleted successfully"}
            afterDelete={() => {
              setOpenDeleteModal(false);
              close();
            }}
          />
          <Button
            type="text"
            danger
            block
            htmlType="button"
            icon={<MdOutlineDeleteForever className="text-lg" />}
            // className="bg-white py-3 min-w-[150px] rounded-r-lg text-[#00086D] font-semibold"
            onClick={() => {
              setOpenDeleteModal(true);
            }}
          >
            Delete Client
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default PopAddNewClientAdmin;
